import React, { useEffect, useState, useMemo } from "react";
import { EyeSvg } from "../../utils/svgComponent";
import Header from "../../components/content_wrapper/header";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import {
  addBoxValidationSchema,
  addBoxinitialValues,
} from "../../validations/boxManagementValidation";
import { toast } from "react-toastify";
import { useAddBox } from "../../hooks/boxManagementApi/addBox";
import { useGetBoxList } from "../../hooks/boxManagementApi/getBoxList";
import Pagination from "../../utils/pagination";
import { useUpdateBoxStatus } from "../../hooks/boxManagementApi/updateStatus";
import { useUpdateBox } from "../../hooks/boxManagementApi/updateBox";
import CustomLaoder from "../../components/customLoader";

const Box_management_listing = () => {
  const [pageNumber, setPageNumber] = useState<any>(1);
  const [countData, setCountData] = useState<any>(20);
  const [searchValue, setSearchValue] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [assignmentFilter, setAssignmentFilter] = useState<string>("all");
  const [modalData, setModalData] = useState({
    open: false,
    modalTitle: "",
    modalType: "",
    id: "",
    usedCubes: "",
  });

  // Debounced search - automatically search as user types
  useEffect(() => {
    const timer = setTimeout(() => {
      setPageNumber(1); // Reset to first page on search
    }, 300);
    return () => clearTimeout(timer);
  }, [searchValue]);

  const { data: boxData, isLoading } = useGetBoxList({
    limit: countData,
    page: pageNumber,
    search: searchValue,
  });

  // Filter boxes based on status and assignment
  const filteredBoxes = useMemo(() => {
    if (!boxData?.data?.allBoxes) return [];
    
    let filtered = [...boxData.data.allBoxes];
    
    // Status filter
    if (statusFilter === "active") {
      filtered = filtered.filter((box: any) => box.status === 1);
    } else if (statusFilter === "suspended") {
      filtered = filtered.filter((box: any) => box.status === 0);
    }
    
    // Assignment filter
    if (assignmentFilter === "assigned") {
      filtered = filtered.filter((box: any) => 
        box.assignedKeyHost || box.assigned_key_host
      );
    } else if (assignmentFilter === "unassigned") {
      filtered = filtered.filter((box: any) => 
        !box.assignedKeyHost && !box.assigned_key_host
      );
    }
    
    return filtered;
  }, [boxData?.data?.allBoxes, statusFilter, assignmentFilter]);

  const handlePrevPage = () => {
    setPageNumber((prevPage: any) => prevPage - 1);
  };

  const handleNextPage = () => {
    setPageNumber((nextPage: any) => nextPage + 1);
  };

  const handleRowLimitChange = (newValue: number) => {
    setCountData(newValue);
    setPageNumber(1);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  const handleClearFilters = () => {
    setSearchValue("");
    setStatusFilter("all");
    setAssignmentFilter("all");
    setPageNumber(1);
  };

  const navigate = useNavigate();

  const handleClick = (id: any) => {
    navigate(`/boxmanagementdetail/${id}`, {
      state: {
        id,
      },
    });
  };

  const handleViewHistory = (id: any) => {
    navigate(`/boxmanagementdetail/${id}`, {
      state: {
        id,
        scrollToAudit: true,
      },
    });
  };

  // Add Box
  const { mutateAsync } = useAddBox();

  // Update Box
  const { mutateAsync: updateBoxMutation } = useUpdateBox();

  const {
    values,
    touched,
    errors,
    handleBlur,
    handleChange,
    handleSubmit,
    setValues,
    resetForm,
  } = useFormik({
    initialValues: addBoxinitialValues,
    validationSchema: addBoxValidationSchema,
    onSubmit: async () => {
      if (modalData.modalType === "Add") {
        try {
          const res: any = await mutateAsync({
            box_id: values.box_id,
            total_keys: values.total_keys,
          });
          if (res?.success === true) {
            toast.success(res?.message || "Box added successfully");
            resetForm();
            setModalData({ ...modalData, open: false });
          } else {
            toast.error(res?.message || "Failed to add box");
          }
        } catch (error: any) {
          toast.error(error?.response?.data?.message || "Error adding box");
        }
      } else if (modalData.modalType === "Edit") {
        try {
          const res: any = await updateBoxMutation({
            id: modalData.id,
            box_id: values.box_id,
            total_keys: values.total_keys,
          });
          if (res?.success === true) {
            toast.success(res?.message || "Box updated successfully");
            resetForm();
            setModalData({ ...modalData, open: false });
          } else {
            toast.error(res?.message || "Failed to update box");
          }
        } catch (error: any) {
          toast.error(error?.response?.data?.message || "Error updating box");
        }
      }
    },
  });

  //Status update
  const { mutateAsync: update } = useUpdateBoxStatus();
  const handleToggleClick = async (id: string, action: number) => {
    let Status = action === 1 ? 0 : 1;
    try {
      const res: any = await update({
        id: id,
        status: Status,
      });
      if (res?.success) {
        toast.success(`Box ${Status === 1 ? 'activated' : 'suspended'} successfully`);
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Failed to update status");
    }
  };

  //return child
  const returnChild = () => {
    if (!modalData.open) {
      return null;
    } else
      return (
        <div
          style={{ backgroundColor: "#0006" }}
          className="modal fade common-modal show d-block"
          tabIndex={-1}
          aria-labelledby="addBoxDetailLabel"
          aria-hidden="true">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-body">
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseModal}
                  aria-label="Close"
                />
                <div className="common-box-body">
                  <h2>{modalData.modalTitle}</h2>
                  <form onSubmit={handleSubmit}>
                    <div className="common-modal-form">
                      <div className="common-modal-field">
                        <label htmlFor="box_id">
                          Box ID <span className="text-danger">*</span>
                        </label>
                        <input
                          id="box_id"
                          disabled={modalData?.id ? true : false}
                          type="text"
                          className={`form-control ${
                            touched.box_id && errors.box_id ? "is-invalid" : ""
                          }`}
                          placeholder="Enter Box ID (e.g., BOX-001)"
                          name="box_id"
                          value={values.box_id}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          autoComplete="off"
                        />
                        {touched.box_id && errors.box_id && (
                          <div className="invalid-feedback d-block">
                            {errors.box_id}
                          </div>
                        )}
                        {modalData?.id && (
                          <small className="form-text text-muted">
                            Box ID cannot be changed after creation
                          </small>
                        )}
                      </div>
                      <div className="common-modal-field">
                        <label htmlFor="total_keys">
                          Total Slots <span className="text-danger">*</span>
                        </label>
                        <input
                          id="total_keys"
                          type="number"
                          min="1"
                          className={`form-control ${
                            touched.total_keys && errors.total_keys
                              ? "is-invalid"
                              : ""
                          }`}
                          placeholder="Enter number of slots"
                          name="total_keys"
                          onChange={handleChange}
                          value={values.total_keys}
                          onBlur={handleBlur}
                        />
                        {touched.total_keys && errors.total_keys && (
                          <div className="invalid-feedback d-block">
                            {errors.total_keys}
                          </div>
                        )}
                        {modalData?.usedCubes && (
                          <small className="form-text text-muted">
                            Currently {modalData.usedCubes} slots are in use
                          </small>
                        )}
                      </div>
                    </div>
                    <div className="common-modal-btns">
                      <button
                        type="button"
                        className="btn secondary-btn"
                        onClick={handleCloseModal}>
                        Cancel
                      </button>
                      <button type="submit" className="btn primary-btn">
                        {modalData.modalType === "Add" ? "Create Box" : "Update Box"}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
  };

  const handleModalOpen = (e: any, data?: any) => {
    if (e.target.title === "Add") {
      setModalData({
        ...modalData,
        modalTitle: "Create New Box",
        open: true,
        modalType: e.target.title,
        id: "",
        usedCubes: "",
      });
    }
    if (e.target.title === "Edit") {
      setModalData({
        ...modalData,
        modalTitle: "Edit Box",
        open: true,
        modalType: e.target.title,
        id: data?._id,
        usedCubes: data?.used_cubicales,
      });
      setValues({
        box_id: data?.box_id,
        total_keys: data?.total_cubicales,
      });
    } else return null;
  };

  const handleCloseModal = () => {
    setModalData({
      ...modalData,
      modalTitle: "",
      open: false,
    });
    resetForm();
  };

  const hasActiveFilters = searchValue || statusFilter !== "all" || assignmentFilter !== "all";

  return (
    <div className="main-wrapper">
      <Header title="Box Management" />
      <CustomLaoder loader={isLoading}>
        <div className="content-wrapper">
          <section className="main-section coach-section spacer">
            <div className="container">
              <div className="wrapper-card">
                <div className="row gy-3">
                  {/* Enhanced Search and Filter Section */}
                  <div className="col-xl-12">
                    <div className="row g-3 align-items-end">
                      {/* Search Box */}
                      <div className="col-lg-4">
                        <label htmlFor="search" className="form-label small text-muted mb-1">
                          Search Boxes
                        </label>
                        <div className="form-block p-relative">
                          <input
                            type="search"
                            className="form-control"
                            id="search"
                            placeholder="Search by Box ID..."
                            value={searchValue}
                            onChange={handleSearchChange}
                            autoComplete="off"
                          />
                          <span className="search-icon" />
                        </div>
                      </div>

                      {/* Status Filter */}
                      <div className="col-lg-2 col-md-4">
                        <label htmlFor="statusFilter" className="form-label small text-muted mb-1">
                          Status
                        </label>
                        <select
                          id="statusFilter"
                          className="form-select"
                          value={statusFilter}
                          onChange={(e) => setStatusFilter(e.target.value)}>
                          <option value="all">All Status</option>
                          <option value="active">Active</option>
                          <option value="suspended">Suspended</option>
                        </select>
                      </div>

                      {/* Assignment Filter */}
                      <div className="col-lg-2 col-md-4">
                        <label htmlFor="assignmentFilter" className="form-label small text-muted mb-1">
                          Assignment
                        </label>
                        <select
                          id="assignmentFilter"
                          className="form-select"
                          value={assignmentFilter}
                          onChange={(e) => setAssignmentFilter(e.target.value)}>
                          <option value="all">All Boxes</option>
                          <option value="assigned">Assigned</option>
                          <option value="unassigned">Unassigned</option>
                        </select>
                      </div>

                      {/* Clear Filters Button */}
                      <div className="col-lg-2 col-md-4">
                        {hasActiveFilters && (
                          <button
                            type="button"
                            className="btn secondary-btn w-100"
                            onClick={handleClearFilters}>
                            <i className="fa-solid fa-filter-circle-xmark me-1" />
                            Clear Filters
                          </button>
                        )}
                      </div>

                      {/* Add Box Button */}
                      <div className="col-lg-2 col-md-12 ms-auto">
                        <button
                          title="Add"
                          onClick={(e) => handleModalOpen(e)}
                          className="btn primary-btn w-100">
                          <i className="fa-solid fa-plus me-1" />
                          Add Box
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Results Summary */}
                  <div className="col-xl-12">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <div>
                        <h2 className="h5 mb-0">Box Listing</h2>
                        <small className="text-muted">
                          Showing {filteredBoxes.length} of {boxData?.data?.allBoxes?.length || 0} boxes
                          {hasActiveFilters && " (filtered)"}
                        </small>
                      </div>
                    </div>

                    {/* Enhanced Table */}
                    <div className="table-blk">
                      <div className="table-responsive">
                        <table id="myTable" className="display border-0">
                          <thead>
                            <tr>
                              <th>Box ID</th>
                              <th className="text-center">Total Slots</th>
                              <th className="text-center">Available</th>
                              <th className="text-center">Occupied</th>
                              <th>Assigned Key Host</th>
                              <th className="text-center">Status</th>
                              <th className="text-center">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredBoxes.length > 0 ? (
                              filteredBoxes.map((data: any, index: number) => (
                                <tr key={data._id || index}>
                                  <td>
                                    <strong>{data?.boxId || data?.box_id}</strong>
                                  </td>
                                  <td className="text-center">
                                    {data?.totalSlots || data?.total_slots || data?.total_cubicales || 0}
                                  </td>
                                  <td className="text-center">
                                    <span className="badge bg-success-subtle text-success">
                                      {data?.availableSlots || data?.available_slots || 0}
                                    </span>
                                  </td>
                                  <td className="text-center">
                                    <span className="badge bg-info-subtle text-info">
                                      {data?.occupiedSlots || data?.occupied_slots || 0}
                                    </span>
                                  </td>
                                  <td>
                                    {data?.assignedKeyHost || data?.assigned_key_host ? (
                                      <span className="text-primary">
                                        {data?.assignedKeyHost || data?.assigned_key_host}
                                      </span>
                                    ) : (
                                      <span className="text-muted fst-italic">Unassigned</span>
                                    )}
                                  </td>
                                  <td className="text-center">
                                    <span
                                      className={`c-pill ${
                                        data?.status === 1
                                          ? "c-pill--success"
                                          : "c-pill--danger"
                                      }`}>
                                      {data?.status === 1 ? "Active" : "Suspended"}
                                    </span>
                                  </td>
                                  <td>
                                    <span className="table-action justify-content-center">
                                      <a
                                        href="#"
                                        onClick={(e) => { e.preventDefault(); handleClick(data.Id || data._id); }}
                                        className="table-eye"
                                        title="View Details">
                                        <EyeSvg />
                                      </a>
                                      <a
                                        href="#"
                                        onClick={(e) => { e.preventDefault(); handleViewHistory(data.Id || data._id); }}
                                        title="View History"
                                        style={{ color: "var(--primary-clr, #6c757d)", fontSize: 16 }}>
                                        <i className="fa-solid fa-clock-rotate-left" />
                                      </a>
                                      <span className="table-check" key={data._id}>
                                        <input
                                          type="checkbox"
                                          id={`toggleEye${data._id}`}
                                          onChange={() =>
                                            handleToggleClick(data._id, data?.status)
                                          }
                                          checked={data?.status === 1 ? true : false}
                                        />
                                        <label
                                          htmlFor={`toggleEye${data._id}`}
                                          title={
                                            data?.status === 1
                                              ? "Click to suspend"
                                              : "Click to activate"
                                          }
                                        />
                                      </span>
                                      <img
                                        title="Edit"
                                        className="table-edit"
                                        onClick={(e) => {
                                          handleModalOpen(e, data);
                                        }}
                                        src="/assets/images/icons/edit_icon.svg"
                                        alt="Edit"
                                      />
                                    </span>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan={7} className="text-center py-5">
                                  <div className="empty-state">
                                    <i className="fa-solid fa-box-open fa-3x text-muted mb-3" />
                                    <h3 className="h5 text-muted">
                                      {hasActiveFilters
                                        ? "No boxes match your filters"
                                        : "No boxes found"}
                                    </h3>
                                    <p className="text-muted mb-3">
                                      {hasActiveFilters
                                        ? "Try adjusting your search or filter criteria"
                                        : "Get started by adding your first box"}
                                    </p>
                                    {!hasActiveFilters && (
                                      <button
                                        title="Add"
                                        onClick={(e) => handleModalOpen(e)}
                                        className="btn primary-btn">
                                        <i className="fa-solid fa-plus me-1" />
                                        Add Your First Box
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>

                      {filteredBoxes.length > 0 && (
                        <Pagination
                          totalPages={boxData?.data?.totalPage}
                          currentPage={pageNumber}
                          handlePrevPage={handlePrevPage}
                          handleNextPage={handleNextPage}
                          handleRowLimitChange={handleRowLimitChange}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
        {returnChild()}
      </CustomLaoder>
    </div>
  );
};

export default Box_management_listing;
